// externals
import { RSAA } from 'redux-api-middleware';

// libraries
import type { CdmsResponseMessageData, StateTree } from '@makemydeal/dr-dash-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// consts
import {
    PUSH_TO_DMS,
    PUSH_TO_DMS_REQUEST,
    PUSH_TO_DMS_SUCCESS,
    PUSH_TO_DMS_FAILURE,
    COPY_DMS_STOCK_NUMBER,
    PUSH_TO_DMS_PLUS,
    PUSH_TO_DMS_ERROR,
    PUSH_TO_DMS_WS_EVENT,
    PUSH_TO_CDMS_RESET_ERROR,
    PUSH_TO_DMS_RESPONSE,
    CDMS_SYNC_BAGGAGE_REQUEST,
    CDMS_SYNC_BAGGAGE_SUCCESS,
    CDMS_SYNC_BAGGAGE_FAILURE
} from '../actionTypes/pushToDmsActionTypes';

// utils
import { apiConfigHelper, locHrefUtil } from '@makemydeal/dr-shared-ui-utils';

// selectors
import { getPushToDmsCorrelationId, getPushToDmsDealXgId, getPushToDmsDealXgVersion } from '../selectors/pushToDmsSelectors';
import { TRACE_ID_HEADER } from '@makemydeal/dr-shared-bff-types';

export const pushToDms = () => {
    return {
        type: PUSH_TO_DMS
    };
};

export const pushToDmsPlus = () => {
    return {
        type: PUSH_TO_DMS_PLUS
    };
};

export const pushToDmsDealApiOnly = () => {
    return {
        type: PUSH_TO_DMS_PLUS,
        meta: {
            dealApiOnly: true
        }
    };
};

export const copyDmsStockNumber = (dmsStockNumber: string) => {
    return {
        type: COPY_DMS_STOCK_NUMBER,
        payload: {
            dmsStockNumber
        }
    };
};

export const dmsError = (statusCode: number, errorDetails?: any) => {
    return {
        type: PUSH_TO_DMS_ERROR,
        payload: {
            errorDetails,
            statusCode
        }
    };
};

export const cleanPushToDmsWsEvent = () => {
    return {
        type: PUSH_TO_DMS_WS_EVENT,
        payload: null
    };
};

export const pushToDmsResponse = (response: CdmsResponseMessageData) => {
    return {
        type: PUSH_TO_DMS_RESPONSE,
        payload: response
    };
};

export const cdmsSyncBaggageRSAA = (appPrefix: string, state: StateTree, response: CdmsResponseMessageData) => {
    const dealXgDealId = getPushToDmsDealXgId(state);
    const correlationId = getPushToDmsCorrelationId(state);

    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, 'cdms-sync-baggage');

    const body = JSON.stringify({
        dealXgDealId,
        dmsDealId: response.dmsDealId,
        dmsName: response.dmsName
    });

    return {
        [RSAA]: {
            endpoint: bffApiUrlResult.url,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', [TRACE_ID_HEADER]: correlationId },
            body,
            types: [CDMS_SYNC_BAGGAGE_REQUEST, CDMS_SYNC_BAGGAGE_SUCCESS, CDMS_SYNC_BAGGAGE_FAILURE]
        }
    };
};

export const cleanPushToCdmsErrorText = () => {
    return {
        type: PUSH_TO_CDMS_RESET_ERROR
    };
};

export const pushToDmsRSAA = (appPrefix: string, state: StateTree) => {
    // Pushing to CDMS should use dealer track override Id as partner-dealer-id
    // if the dealer has DT override enabled in Accelerate's Admin
    const dealerTrackOverrideEnabled = dealerSelectors.getLeadRoutingOverrideSettingsEnabled(state);
    const dealertrackOverrideId = `${dealerSelectors.getLeadOverrideDealerId(state)}`;

    const dealerId =
        dealerTrackOverrideEnabled && dealertrackOverrideId ? dealertrackOverrideId : dealerSelectors.getDealerId(state).toString();
    const dealXgId = getPushToDmsDealXgId(state);
    const dealXgVersion = getPushToDmsDealXgVersion(state);
    const correlationId = getPushToDmsCorrelationId(state);
    const isEnableOptionalPbcUi = featureToggleSelectors.enableOptionalPbcUi(state);
    const enableCdmsSyncFlow = featureToggleSelectors.enableCdmsSyncFlow(state);

    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, 'cdms');
    const queryParams: string[] = [];
    if (isEnableOptionalPbcUi) queryParams.push('mode=storefront');
    if (enableCdmsSyncFlow) queryParams.push('syncflow=true');
    const endpoint = bffApiUrlResult.url + (queryParams.length ? `?${queryParams.join('&')}` : '');

    const body = JSON.stringify({
        dealerId,
        dealXgId,
        dealXgVersion
    });

    return {
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', [TRACE_ID_HEADER]: correlationId },
            body,
            types: [PUSH_TO_DMS_REQUEST, PUSH_TO_DMS_SUCCESS, PUSH_TO_DMS_FAILURE]
        }
    };
};
